import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import Layout from "../components/app/Layout";
import { useAuth } from "../contexts/AuthContext";
import { useI18n } from "../contexts/I18nContext";
import api, { describeAxiosError } from "../lib/api";
import Avatar from "../components/app/Avatar";
import { Users, UserPlus, Trash2, Sparkles, Mail, MapPin, Trophy } from "lucide-react";
import { toast } from "sonner";

/**
 * /academy/coaches — dedicated page for academy role to manage their
 * coaching roster (invite, remove, view). Pulled out of AcademyDashboard
 * (Iter 29) so the academy nav reflects strict role-based separation.
 */
export default function AcademyCoachesPage() {
  const { user } = useAuth();
  const { lang } = useI18n();
  const [data, setData] = useState(null);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteBusy, setInviteBusy] = useState(false);
  const [inviteError, setInviteError] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/academy/overview");
      setData(data);
    } catch (e) {
      setData({ error: describeAxiosError(e) });
    }
  };
  useEffect(() => { load(); }, []);

  if (user && user.role !== "academy") return <Navigate to="/" replace />;
  if (!data) return <Layout><div className="min-h-[50vh] flex items-center justify-center"><div className="pitch-loader"><div className="ball" /></div></div></Layout>;

  const coaches = data.coaches || [];

  const invite = async (e) => {
    e.preventDefault();
    setInviteBusy(true); setInviteError("");
    try {
      await api.post("/academy/coaches", { email: inviteEmail.trim().toLowerCase() });
      toast.success(lang === "bg" ? "Треньорът е добавен" : "Coach added");
      setInviteEmail("");
      await load();
    } catch (e2) {
      setInviteError(describeAxiosError(e2));
    }
    setInviteBusy(false);
  };

  const removeCoach = async (id) => {
    if (!window.confirm(lang === "bg" ? "Премахни този треньор от академията?" : "Remove this coach?")) return;
    try { await api.delete(`/academy/coaches/${id}`); await load(); toast.success(lang === "bg" ? "Премахнат" : "Removed"); } catch (_) {}
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="academy-coaches-page">
        <div>
          <div className="label-mono mb-1">// {lang === "bg" ? "Треньорски състав" : "Coaching staff"}</div>
          <h1 className="font-oswald uppercase text-3xl sm:text-4xl tracking-tight">
            {lang === "bg" ? "Треньори" : "Coaches"}
          </h1>
          <p className="text-xs text-white/45 font-mono mt-1">
            {lang === "bg" ? "Управлявай треньорите към твоята академия." : "Manage the coaches attached to your academy."}
          </p>
        </div>

        <form onSubmit={invite} className="surface-elevated p-5 space-y-2" data-testid="academy-coaches-invite-form">
          <div className="label-mono mb-1">// {lang === "bg" ? "Покани треньор" : "Invite coach"}</div>
          <div className="flex gap-2 flex-col sm:flex-row">
            <div className="relative flex-1">
              <Mail className="w-4 h-4 text-white/30 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="email"
                required
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                placeholder={lang === "bg" ? "коуч@email.com" : "coach@email.com"}
                className="w-full bg-[#0A1628] border border-white/10 pl-9 pr-3 py-2.5 focus:border-pitch outline-none rounded"
                data-testid="academy-coaches-invite-email"
              />
            </div>
            <button
              type="submit"
              disabled={inviteBusy}
              className="btn-primary px-4 py-2.5 text-xs inline-flex items-center justify-center gap-2 disabled:opacity-50"
              data-testid="academy-coaches-invite-submit"
            >
              {inviteBusy ? <Sparkles className="w-4 h-4 animate-pulse" /> : <UserPlus className="w-4 h-4" />}
              {lang === "bg" ? "Покани" : "Invite"}
            </button>
          </div>
          {inviteError && <div className="text-red-400 text-xs font-mono">{inviteError}</div>}
          <div className="text-[10px] text-white/30 font-mono">
            {lang === "bg" ? "Треньорът трябва вече да има акаунт в ONTHEPITCH." : "The coach must already have an ONTHEPITCH account."}
          </div>
        </form>

        <div className="card-sharp p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-oswald uppercase text-2xl tracking-tight flex items-center gap-2">
              <Users className="w-5 h-5 text-pitch" /> {lang === "bg" ? "Активни треньори" : "Active coaches"}
            </h2>
            <span className="label-mono">{coaches.length}</span>
          </div>
          {coaches.length === 0 ? (
            <div className="text-white/35 text-xs font-mono tracking-widest py-12 text-center" data-testid="academy-coaches-empty">
              {lang === "bg" ? "Все още няма треньори. Покани първия." : "No coaches yet. Invite your first."}
            </div>
          ) : (
            <div className="space-y-2">
              {coaches.map((c) => (
                <div key={c.id} className="surface-1 p-3 flex items-center gap-4" data-testid={`academy-coach-row-${c.id}`}>
                  <Avatar name={c.name} src={c.photo_url} size="md" />
                  <div className="flex-1 min-w-0">
                    <div className="font-oswald uppercase tracking-tight text-base truncate">{c.name}</div>
                    <div className="text-[11px] text-white/45 font-mono flex items-center gap-3 mt-0.5 flex-wrap">
                      {c.city && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {c.city}</span>}
                      {c.specialty && <span className="chip-badge text-[9px] py-0.5 px-2">{c.specialty}</span>}
                      {c.hourly_rate != null && <span className="text-pitch">€{c.hourly_rate}/h</span>}
                      {c.rating != null && c.rating > 0 && <span className="text-white/65 inline-flex items-center gap-1"><Trophy className="w-3 h-3 text-pitch" /> {c.rating.toFixed(1)}</span>}
                    </div>
                  </div>
                  {(c.badges || []).includes("verified") && (
                    <span className="w-6 h-6 bg-pitch text-white rounded-full flex items-center justify-center text-[10px] font-bold" title="Verified">✓</span>
                  )}
                  <button
                    onClick={() => removeCoach(c.id)}
                    className="p-2 text-white/40 hover:text-red-400 transition-colors"
                    data-testid={`academy-coach-remove-${c.id}`}
                    aria-label="Remove coach"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
